import { memo } from 'react'
import { CheckIcon, InfoIcon } from '@/components/icons'
import { AudienceIcon } from '@/components/mediaBuying/adCampaign/AudienceIcon'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { isAudienceExcluded, getExclusionReason } from '@/utils/ad-campaigns-helpers'

const AudienceCard = memo(({ audience, isSelected, onToggleSelection, pixelIsActive }) => {
    const isExcluded = isAudienceExcluded(audience, pixelIsActive)
    const exclusionReason = getExclusionReason(audience, pixelIsActive)

    const handleCardClick = () => {
        if (!isExcluded) {
            onToggleSelection(audience.id)
        }
    }

    const getTags = () => {
        const tags = []

        // Funnel stage tag with color
        const stageColors = {
            acquisition: '#37A0E6',
            reengagement: '#41D8B3',
            retargeting: '#41D8B3',
            retention: '#DC402D',
        }

        tags.push({
            label: audience.funnel_stage.charAt(0).toUpperCase() + audience.funnel_stage.slice(1),
            color: stageColors[audience.funnel_stage] || '#666',
            filled: true,
        })

        // Lookalike tag
        if (audience.lookalike) {
            tags.push({
                label: 'Lookalike',
                color: '#37A0E6',
                filled: false,
            })
        }

        // AI tag
        if (audience.ai) {
            tags.push({
                label: 'AI',
                color: '#41D8B3',
                filled: false,
            })
        }

        return tags
    }

    const cardContent = (
        <div
            className={`tw:h-[264px] tw:bg-[#17273C] tw:rounded-[2px] tw:p-3.5 tw:relative tw:transition-all tw:duration-200 tw:border-2 tw:flex tw:flex-col ${
                isExcluded
                    ? 'tw:border-gray-600'
                    : isSelected
                      ? 'tw:border-[#2450BF] tw:bg-[#1E3247] tw:cursor-pointer'
                      : 'tw:border-transparent hover:tw:bg-[#243447] tw:cursor-pointer'
            }`}
            onClick={handleCardClick}
        >
            {/* Content wrapper */}
            <div className="tw:flex-1 tw:flex tw:flex-col">
                {/* Selection checkbox */}
                <div
                    className={`tw:absolute tw:top-4 tw:left-4 tw:w-6 tw:h-6 tw:rounded tw:border-2 tw:flex tw:items-center tw:justify-center ${
                        isExcluded
                            ? 'tw:border-gray-500 tw:bg-gray-700 tw:hidden'
                            : isSelected
                              ? 'tw:bg-[#2450BF] tw:border-[#2450BF]'
                              : 'tw:border-[#2450BF] tw:bg-transparent'
                    }`}
                >
                    {isSelected && !isExcluded && <CheckIcon className="tw:w-4.5 tw:h-4.5 tw:text-white" />}
                </div>

                {/* Content that can grow */}
                <div className="tw:flex-1 tw:flex tw:flex-col">
                    {/* Audience icon and name */}
                    <div className={`tw:flex tw:gap-2 tw:pt-1 ${isExcluded ? '' : 'tw:ml-9'}`}>
                        {audience.icon && <AudienceIcon icon={audience.icon} className="tw:size-7" />}
                        <span
                            className={`tw:!font-semibold tw:!text-sm tw:flex-1 tw:!mt-0 ${isExcluded ? 'tw:text-gray-200' : 'tw:text-white'}`}
                        >
                            {audience.name}
                        </span>
                    </div>

                    {/* Tags */}
                    <div className="tw:flex tw:gap-2 tw:flex-wrap tw:mt-3 tw:mb-3">
                        {getTags().map((tag, index) => (
                            <span
                                key={`${tag.label}-${index}`}
                                className={`tw:px-3 tw:py-1 tw:rounded-full tw:text-sm tw:font-medium ${
                                    tag.filled
                                        ? isExcluded
                                            ? 'tw:text-gray-200'
                                            : 'tw:text-white'
                                        : 'tw:bg-transparent tw:border'
                                }`}
                                style={{
                                    backgroundColor: tag.filled ? (isExcluded ? '#374151' : tag.color) : 'transparent',
                                    borderColor: !tag.filled ? (isExcluded ? '#e5e7eb' : tag.color) : 'transparent',
                                    color: isExcluded ? '#e5e7eb' : !tag.filled ? tag.color : 'white',
                                }}
                            >
                                {tag.label}
                            </span>
                        ))}
                    </div>

                    {/* Description */}
                    <p
                        className={`tw:text-sm tw:mt-3 tw:line-clamp-2 ${isExcluded ? 'tw:text-gray-200' : 'tw:text-[#899CBD]'}`}
                    >
                        {audience.description}
                    </p>

                    {/* Warning message for excluded audiences */}
                    {isExcluded && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="tw:flex tw:gap-2 tw:cursor-default tw:-mt-2 tw:max-w-[240px]">
                                    <InfoIcon className="tw:w-5 tw:h-5 tw:text-yellow-300 tw:flex-shrink-0 " />
                                    <span className="tw:text-sm tw:text-yellow-300 tw:font-thin">
                                        Audience unavailable because your Pixel is inactive. <br />
                                        <span className="tw:cursor-pointer tw:font-bold">Learn more</span>
                                    </span>
                                </div>
                            </TooltipTrigger>
                            <TooltipContent
                                showArrow={false}
                                className="tw:max-w-[360px] tw:rounded-none tw:px-4 tw:pl-5 tw:pt-4 tw:bg-gray-800 tw:bg-opacity-100"
                            >
                                {exclusionReason && Array.isArray(exclusionReason) ? (
                                    <div className="tw:space-y-3">
                                        {exclusionReason.map((paragraph, index) => (
                                            <p key={index} className="tw:text-sm">
                                                {paragraph}
                                            </p>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="tw:text-sm">{exclusionReason}</p>
                                )}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Metrics - pinned to bottom */}
            <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-[#899CBD] tw:mt-auto tw:pt-3">
                {((audience.live_count || 0) > 0 || (audience.created_count || 0) > 0) && (
                    <span
                        className={`tw:w-2.5 tw:h-2.5 tw:rounded-full tw:inline-block ${
                            (audience.live_count || 0) > 0 ? 'tw:bg-[#4DD15E]' : 'tw:outline-2 tw:outline-[#3B5168]'
                        }`}
                    ></span>
                )}
                <span>
                    {audience.created_count || 0} created • {audience.live_count || 0} live
                </span>
            </div>
        </div>
    )

    return cardContent
})

AudienceCard.displayName = 'AudienceCard'

export default AudienceCard
